/**
 * CustomerModalHeader - Header compacto do modal premium de cliente
 * Layout horizontal com status, nome, tipo e ações
 * 
 * CHANGELOG:
 *   RELEASE: 2026-09-03 v1.5.0 - Selo Cliente/Contato sempre visível (inclusive mobile)
 *            e ação de edição com rótulo explícito ("Editar cliente"/"Editar contato").
 *   RELEASE: 2026-08-20 v1.4.0 - Responsividade mobile: badges secundários ocultos em telas
 *            pequenas e ação "Transformar em cliente" apenas com ícone no mobile.
 *   RELEASE: 2026-08-19 v1.3.0 - Ação "Transformar em cliente" no modo Contato
 * @version 1.5.0
 * RELEASE: 2026-01-10
 */
import React from 'react';
import { 
  Pencil, 
  X,
  User,
  BadgeCheck
} from 'lucide-react';
import { CrmCustomer } from '@/hooks/useCrmCustomers';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';


import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// Props do componente
interface CustomerModalHeaderProps {
  customer: CrmCustomer;
  onEdit: () => void;
  onClose: () => void;
  /**
   * v1.1.0: Modo Contato — contato sem cliente vinculado.
   */
  isContactMode?: boolean;
  /**
   * v1.3.0: Handler de conversão contato → cliente.
   */
  onConvertToCustomer?: () => void;
}

/**
 * Obtém o nome de exibição do cliente
 */
function getDisplayName(customer: CrmCustomer): string {
  if (customer.customer_type === 'pf') {
    return customer.nome || 'Sem nome';
  }
  return customer.nome_fantasia || customer.razao_social || 'Sem nome';
}

/**
 * Header compacto do modal (48px)
 */

export function CustomerModalHeader({
  customer,
  onEdit,
  onClose,
  isContactMode = false
}: CustomerModalHeaderProps) {

  const displayName = getDisplayName(customer);
  const isPF = customer.customer_type === 'pf';

  return (
    <div className="flex items-center gap-3 px-4 h-12 border-b bg-background shrink-0">
      {/* Badge de status removido: cliente é sempre Ativo */}


      {/* Badge/ícone de tipo — no modo Contato indica "Contato" */}
      {/* v1.5.0: selo permanente (também no mobile) deixando claro se o
          registro já é um Cliente cadastrado ou apenas um Contato */}
      {isContactMode ? (
        <Badge variant="outline" className="shrink-0 gap-1 font-medium text-xs bg-sky-500/10 text-sky-600 border-sky-500/20">
          <User className="h-3 w-3" />
          Contato
        </Badge>
      ) : (
        // v1.7.0: selo do cliente em verde suave, sem borda, alinhado aos demais badges
        <Badge className="shrink-0 gap-1 font-medium text-xs bg-success/10 text-success hover:bg-success/15 border-transparent">
          <BadgeCheck className="h-3 w-3" />
          <span>Cliente</span>
          <span className="hidden sm:inline opacity-80">· {isPF ? 'PF' : 'PJ'}</span>
        </Badge>
      )}


      {/* Nome truncado */}
      <h2 className="text-sm font-semibold truncate flex-1 min-w-0">
        {displayName}
      </h2>

      {/* Separador visual */}
      <div className="h-4 w-px bg-border shrink-0" />


      {/* Botões de ação como ícones */}
      <div className="flex items-center gap-0.5 shrink-0">
        {/* Editar */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-8 gap-1.5 px-2 sm:px-3"
              onClick={onEdit}
            >
              <Pencil className="h-3.5 w-3.5" />
              {/* v1.5.0: rótulo visível no desktop; no mobile fica só o ícone */}
              <span className="hidden sm:inline text-xs">
                {isContactMode ? 'Editar contato' : 'Editar cliente'}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs">{isContactMode ? 'Editar contato' : 'Editar cliente'}</p>
          </TooltipContent>
        </Tooltip>

        {/* v1.6.0: a ação "Transformar em cliente" foi movida para a sidebar
            (logo abaixo do nome do contato) para melhor leitura dos dados. */}


        {/* Ações de status/exclusão removidas: cliente é sempre Ativo */}



        {/* Separador */}
        <div className="h-4 w-px bg-border mx-1" />

        {/* Fechar */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 hover:bg-muted"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs">Fechar</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
